import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { useEffect, useRef, useState } from "react";
import { Fetch } from "../../../shared/Fetch";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { Filter, ISalEffFilter, EmptySalEffFilter } from "./Filter";
import { SubTable } from "./SubTable";
import { ILogin, IPermissionProps } from "../../../models/Login";
import { Accordion, AccordionTab } from "primereact/accordion";
import { format } from "date-fns";
import { MultiSelect } from "primereact/multiselect";
import { safeFormatDate } from "../../../models/DateFormatMapper";

interface ISalesEfficiency {
    login_ID?: number,
    sales?: string,
    date_Of_Joining?: Date,
    branchName?: string,
    noOfReq?: number,
    noOfSub?: number,
    noOfSubPV?: number,
    noOfSubClient?: number,
    noOfSubRejSales?: number,
    noOfSubRejPV?: number,
    noOfSubRejClient?: number,
    noOfECI?: number,
    noStatusNotChange?: number,
    served?: number,
    unserved?: number,
    per?: number,
    noOfContact?: number,
    po?: number,
    noRoomECS?: number,
    reqHoldPV?: number,
    noOfReqHold?: number,
    noOfReqRej?: number,
    noOfReqHoldS?: number,
    noOfReqRejRH?: number,
}

const columns: string[] = [
    "Sales Manager",
    "DOJ",
    "Tenure (M)",
    "Team Manager",
    "Resource Manager",
    "Sales Comp(Branch)",
    "No. Of Client",
    "#Req",
    "NORHS",
    "NORRRH",
    "#Served",
    "#Per(%)",
    "#Unserved",
    "#Sub",
    "#SubPV",
    "#SubClient",
    "#SubRejSales",
    "#SubRejPV",
    "#SubRejClient",
    "#ECI",
    "#StatusNotChange",
    "#PO",
    "NoRoomForECS",
    "ReqHoldByPV",
];

export const SalesEfficiency = (permissions: IPermissionProps) => {
    const [tableData, setTableData] = useState({ tableData: [], loading: false } as { tableData: Array<ISalesEfficiency>, loading: boolean });
    const [tFooter, setTFooter] = useState<ISalesEfficiency>({});
    const [salEffFilter, setSalEffFilter] = useState<ISalEffFilter>(EmptySalEffFilter);
    const [visibleColumns, setVisibleColumns] = useState<string[]>(columns);
    const [shouldLoad, setShouldLoad] = useState(false);
    const dt = useRef<any>(null);
    const toast = useRef<any>(null);

    const _roles: Array<number> = [];
    const loginObj: ILogin = JSON.parse(sessionStorage.getItem('auth') + '');
    const DateFormat = loginObj?.date_Format;
    loginObj.roles.forEach(role => {
        _roles.push(role.role_ID);
    });

    const [subTable, setSubTable] = useState({ open: false, salesID: 0, resType: 0 } as { open: boolean, salesID?: number, resType: number });
    const hideSubTableDialog = () => {
        setSubTable({ open: false, salesID: 0, resType: 0 });
    };

    useEffect(() => {
        if (shouldLoad) {
            loadTableData();
            setShouldLoad(false);
        }
    }, [shouldLoad]);

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const loadTableData = async () => {
        setTableData({ tableData: tableData.tableData, loading: true });
        const filter = { ...salEffFilter };
        filter.fromDate = filter.fromDate ? format(new Date(filter.fromDate), 'yyyy-MM-dd') : null;
        filter.toDate = filter.toDate ? format(new Date(filter.toDate), 'yyyy-MM-dd') : null;
        const response = await Fetch("Report_Sales_Efficiency", "GET", null, `filter=${encodeURIComponent(JSON.stringify(filter))}`, toast);
        if (response) {
            const data: Array<ISalesEfficiency> = await response.json();

            const _data: Array<ISalesEfficiency> = [];
            let totalNoOfReq = 0;
            let totalNoOfSub = 0;
            let totalNoOfSubPV = 0;
            let totalNoOfSubClient = 0;
            let totalNoOfSubRejSales = 0;
            let totalNoOfSubRejPV = 0;
            let totalNoOfSubRejClient = 0;
            let totalNoOfECI = 0;
            let totalNoStatusNotChange = 0;
            let totalNoServed = 0;
            let totalNounserved = 0;
            let totalNoPer = 0;
            let totalNoOfContact = 0;

            data.forEach((d, i) => {
                if (d.noOfReq) totalNoOfReq += d.noOfReq;
                if (d.noOfSub) totalNoOfSub += d.noOfSub;
                if (d.noOfSubPV) totalNoOfSubPV += d.noOfSubPV;
                if (d.noOfSubClient) totalNoOfSubClient += d.noOfSubClient;
                if (d.noOfSubRejSales) totalNoOfSubRejSales += d.noOfSubRejSales;
                if (d.noOfSubRejPV) totalNoOfSubRejPV += d.noOfSubRejPV;
                if (d.noOfSubRejClient) totalNoOfSubRejClient += d.noOfSubRejClient;
                if (d.noOfECI) totalNoOfECI += d.noOfECI;
                if (d.noStatusNotChange) totalNoStatusNotChange += d.noStatusNotChange;
                if (d.served) totalNoServed += d.served;
                if (d.unserved) totalNounserved += d.unserved;
                d.per = d.noOfReq ? (((d.served ?? 0) * 100) / d.noOfReq) : 0
                if (d.per) totalNoPer += d.per;
                if (d.noOfContact) totalNoOfContact += d.noOfContact;
                _data.push(d);
            });

            const totalRecords: ISalesEfficiency = {
                sales: "Total Records",
                noOfReq: totalNoOfReq,
                noOfSub: totalNoOfSub,
                noOfSubPV: totalNoOfSubPV,
                noOfSubClient: totalNoOfSubClient,
                noOfSubRejSales: totalNoOfSubRejSales,
                noOfSubRejPV: totalNoOfSubRejPV,
                noOfSubRejClient: totalNoOfSubRejClient,
                noOfECI: totalNoOfECI,
                noStatusNotChange: totalNoStatusNotChange,
                served: totalNoServed,
                unserved: totalNounserved,
                per: totalNoPer,
                noOfContact: totalNoOfContact,
            };
            setTFooter(totalRecords);

            setTableData({ tableData: _data, loading: false });
        }
    }

    const onColumnToggle = (event: any) => {
        let selectedColumns = event.value;
        let orderedSelectedColumns = columns.filter((col) => selectedColumns.some((sCol: string) => sCol === col));
        setVisibleColumns(orderedSelectedColumns);
    };

    const rightToolbarTemplate = () => {
        const loginObj: ILogin = JSON.parse(sessionStorage.getItem('auth') + '');
        return (
            permissions.is_Export || loginObj.is_Team_Lead ?
                <React.Fragment>
                    <MultiSelect value={visibleColumns} options={columns} optionLabel="" onChange={onColumnToggle} className="w-full sm:w-20rem" display="chip" />
                    <Button label="Export" icon="pi pi-upload" className="p-button-help" onClick={exportCSV} />
                </React.Fragment> : <MultiSelect value={visibleColumns} options={columns} optionLabel="" onChange={onColumnToggle} className="w-full sm:w-20rem" display="chip" />
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">Sales Efficiency Report</h5>
        </div>
    );

    const noOfSub = (rowData: ISalesEfficiency) => <span onClick={() => rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 1 })} className="cursor-pointer text-cyan-700 underline">{rowData.noOfSub}</span>
    const noOfContact = (rowData: ISalesEfficiency) => <span onClick={() => { (!_roles.includes(40)) ? rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 11 }) : setSubTable({ open: false, salesID: rowData.login_ID, resType: 11 }) }} className="cursor-pointer text-cyan-700 underline">{rowData.noOfContact}</span>
    const noOfSubPV = (rowData: ISalesEfficiency) => <span onClick={() => rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 2 })} className="cursor-pointer text-cyan-700 underline">{rowData.noOfSubPV}</span>
    const noOfSubClient = (rowData: ISalesEfficiency) => <span onClick={() => rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 3 })} className="cursor-pointer text-cyan-700 underline">{rowData.noOfSubClient}</span>
    const noOfSubRejSales = (rowData: ISalesEfficiency) => <span onClick={() => rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 4 })} className="cursor-pointer text-cyan-700 underline">{rowData.noOfSubRejSales}</span>
    const noOfSubRejPV = (rowData: ISalesEfficiency) => <span onClick={() => rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 5 })} className="cursor-pointer text-cyan-700 underline">{rowData.noOfSubRejPV}</span>
    const noOfSubRejClient = (rowData: ISalesEfficiency) => <span onClick={() => rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 6 })} className="cursor-pointer text-cyan-700 underline">{rowData.noOfSubRejClient}</span>
    const noOfECI = (rowData: ISalesEfficiency) => <span onClick={() => rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 7 })} className="cursor-pointer text-cyan-700 underline">{rowData.noOfECI}</span>
    const noStatusNotChange = (rowData: ISalesEfficiency) => <span onClick={() => rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 8 })} className="cursor-pointer text-cyan-700 underline">{rowData.noStatusNotChange}</span>
    const served = (rowData: ISalesEfficiency) => <span onClick={() => rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 9 })} className="cursor-pointer text-cyan-700 underline">{rowData.served}</span>
    const unserved = (rowData: ISalesEfficiency) => <span onClick={() => rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 20 })} className="cursor-pointer text-cyan-700 underline">{rowData.unserved}</span>
    const noRoomForECS = (rowData: ISalesEfficiency) => <span onClick={() => rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 14 })} className="cursor-pointer text-cyan-700 underline">{rowData.noRoomECS}</span>
    const reqHoldByPV = (rowData: ISalesEfficiency) => <span onClick={() => rowData.login_ID && setSubTable({ open: true, salesID: rowData.login_ID, resType: 15 })} className="cursor-pointer text-cyan-700 underline">{rowData.reqHoldPV}</span>

    return (
        <>
            <Toolbar className="mb-1 py-0" right={rightToolbarTemplate}></Toolbar>
            <Accordion>
                <AccordionTab header="Filter">
                    <Filter setSalEffFilter={setSalEffFilter} onSearch={() => setShouldLoad(true)} toast={toast} />
                </AccordionTab>
            </Accordion>
            <DataTable style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0", marginTop: "5px" }}
                ref={dt} size="small" stripedRows showGridlines resizableColumns columnResizeMode="expand" scrollable scrollHeight="65vh" exportFilename={format(new Date(), 'dd-MM-yyyy hh_mm_ss a').toUpperCase()}
                value={tableData.tableData}
                header={header}
                loading={tableData.loading}
            >
                <Column field="sn" header="SN" body={(rowData, options) => options.rowIndex + 1} footer={tFooter.sales} />
                {visibleColumns.includes("Sales Manager") && <Column style={{ minWidth: "15rem" }} field="sales" header="Sales Manager" sortable body={(rowData) => <p style={{ color: rowData.user_Status ? "red" : "" }}>{rowData.sales}</p>} />}
                {visibleColumns.includes("DOJ") && <Column style={{ minWidth: "8rem" }} field="date_Of_Joining" header="DOJ" sortable body={(rowData) => <p>{safeFormatDate(rowData.date_Of_Joining, "dd/MM/yyyy", DateFormat)}</p>} />}
                {visibleColumns.includes("Tenure (M)") && <Column field="working_Months" header="Tenure (M)" />}
                {visibleColumns.includes("Team Manager") && <Column field="teamManager" header="Team Manager" sortable />}
                {visibleColumns.includes("Resource Manager") && <Column field="resourceManagerName" header="Resource Manager" sortable />}
                {visibleColumns.includes("Sales Comp(Branch)") && <Column style={{ minWidth: "9rem" }} field="branchName" header="Sales Comp(Branch)" sortable />}
                {visibleColumns.includes("No. Of Client") && <Column field="noOfContact" header="No. Of Client" sortable body={noOfContact}
                    footer={() => {
                        const noOfContact = tableData.tableData.reduce((sum, item) => sum + (item.noOfContact || 0), 0);
                        return <span
                            onClick={() => { (_roles.includes(1) || _roles.includes(20) || _roles.includes(26)) ? setSubTable({ open: true, resType: 11 }) : setSubTable({ open: false, resType: 11 }) }}
                            className="cursor-pointer text-cyan-700 underline">{noOfContact}</span>;
                    }} />}

                {visibleColumns.includes("#Req") && <Column field="noOfReq" header="#Req" sortable footer={tFooter.noOfReq} />}
                {visibleColumns.includes("NORHS") && <Column field="noOfReqHoldS" header="NORHS" sortable footer={() => {
                    const reqHoldPV = tableData.tableData.reduce((sum, item) => sum + (item.noOfReqHoldS || 0), 0);
                    return <span
                        className="cursor-pointer ">{reqHoldPV}</span>;
                }} />}
                {visibleColumns.includes("NORRRH") && <Column field="noOfReqRejRH" header="NORRRH" sortable footer={() => {
                    const reqHoldPV = tableData.tableData.reduce((sum, item) => sum + (item.noOfReqRejRH || 0), 0);
                    return <span
                        className="cursor-pointer ">{reqHoldPV}</span>;
                }} />}
                {visibleColumns.includes("#Served") && <Column field="served" header="#Served" sortable body={served} footer={() => {
                    const Served = tableData.tableData.reduce((sum, item) => sum + (item.served || 0), 0);
                    return <span
                        onClick={() => setSubTable({ open: true, resType: 9 })}
                        className="cursor-pointer text-cyan-700 underline">{Served}</span>;
                }} />}
                {visibleColumns.includes("#Per(%)") && <Column field="per" header="#Per(%)" sortable
                    body={(row: ISalesEfficiency) => <span>{row.per?.toFixed(2)}</span>}
                    footer={() => {
                        const noOfReq = tableData.tableData.reduce((sum, item) => sum + (item.noOfReq || 0), 0);
                        const served = tableData.tableData.reduce((sum, item) => sum + (item.served || 0), 0);
                        const percentage: number = (served / noOfReq) * 100;
                        return <span>
                            {percentage ? percentage.toFixed(2) : '0.00'}
                        </span>
                    }} />}
                {visibleColumns.includes("#Unserved") && <Column field="unserved" header="#Unserved" sortable body={unserved}
                    footer={() => {
                        const Unserved = tableData.tableData.reduce((sum, item) => sum + (item.unserved || 0), 0);
                        return <span
                            onClick={() => setSubTable({ open: true, resType: 20 })}
                            className="cursor-pointer text-cyan-700 underline">{Unserved}</span>;
                    }}
                />}
                {visibleColumns.includes("#Sub") && <Column field="noOfSub" header="#Sub" sortable body={noOfSub}
                    footer={() => {
                        const Sub = tableData.tableData.reduce((sum, item) => sum + (item.noOfSub || 0), 0);
                        return <span
                            onClick={() => setSubTable({ open: true, resType: 1 })}
                            className="cursor-pointer text-cyan-700 underline">{Sub}</span>;
                    }}
                />}

                {visibleColumns.includes("#SubPV") && <Column field="noOfSubPV" header="#SubPV" sortable body={noOfSubPV}
                    footer={() => {
                        const SubPV = tableData.tableData.reduce((sum, item) => sum + (item.noOfSubPV || 0), 0);
                        return <span
                            onClick={() => setSubTable({ open: true, resType: 2 })}
                            className="cursor-pointer text-cyan-700 underline">{SubPV}</span>;
                    }}
                />}

                {visibleColumns.includes("#SubClient") && <Column field="noOfSubClient" header="#SubClient" sortable body={noOfSubClient}
                    footer={() => {
                        const SubClient = tableData.tableData.reduce((sum, item) => sum + (item.noOfSubClient || 0), 0);
                        return <span
                            onClick={() => setSubTable({ open: true, resType: 3 })}
                            className="cursor-pointer text-cyan-700 underline">{SubClient}</span>;
                    }} />}

                {visibleColumns.includes("#SubRejSales") && <Column field="noOfSubRejSales" header="#SubRejSales" sortable body={noOfSubRejSales}
                    footer={() => {
                        const SubRejSales = tableData.tableData.reduce((sum, item) => sum + (item.noOfSubRejSales || 0), 0);
                        return <span
                            onClick={() => setSubTable({ open: true, resType: 4 })}
                            className="cursor-pointer text-cyan-700 underline">{SubRejSales}</span>;
                    }}

                />}

                {visibleColumns.includes("#SubRejPV") && <Column field="noOfSubRejPV" header="#SubRejPV" sortable body={noOfSubRejPV}
                    footer={() => {
                        const SubRejPV = tableData.tableData.reduce((sum, item) => sum + (item.noOfSubRejPV || 0), 0);
                        return <span
                            onClick={() => setSubTable({ open: true, resType: 5 })}
                            className="cursor-pointer text-cyan-700 underline">{SubRejPV}</span>;
                    }}
                />}

                {visibleColumns.includes("#SubRejClient") && <Column field="noOfSubRejClient" header="#SubRejClient" sortable body={noOfSubRejClient}
                    footer={() => {
                        const SubRejClient = tableData.tableData.reduce((sum, item) => sum + (item.noOfSubRejClient || 0), 0);
                        return <span
                            onClick={() => setSubTable({ open: true, resType: 6 })}
                            className="cursor-pointer text-cyan-700 underline">{SubRejClient}</span>;
                    }}
                />}

                {visibleColumns.includes("#ECI") && <Column field="noOfECI" header="#ECI" sortable body={noOfECI} footer={() => {
                    const ECI = tableData.tableData.reduce((sum, item) => sum + (item.noOfECI || 0), 0);
                    return <span
                        onClick={() => setSubTable({ open: true, resType: 7 })}
                        className="cursor-pointer text-cyan-700 underline">{ECI}</span>;
                }}
                />}

                {visibleColumns.includes("#StatusNotChange") && <Column field="noStatusNotChange" header="#StatusNotChange" sortable body={noStatusNotChange} footer={() => {
                    const StatusNotChange = tableData.tableData.reduce((sum, item) => sum + (item.noStatusNotChange || 0), 0);
                    return <span
                        onClick={() => setSubTable({ open: true, resType: 8 })}
                        className="cursor-pointer text-cyan-700 underline">{StatusNotChange}</span>;
                }} />}

                {visibleColumns.includes("#PO") && <Column field="po" header="#PO" sortable footer={() => {
                    const noOFPO = tableData.tableData.reduce((sum, item) => sum + (item.po || 0), 0);
                    return <span
                        className="cursor-pointer">{noOFPO}</span>;
                }} />}
                {visibleColumns.includes("NoRoomForECS") && <Column field="noRoomECS" header="NoRoomForECS" body={noRoomForECS} sortable footer={() => {
                    const noRoomECS = tableData.tableData.reduce((sum, item) => sum + (item.noRoomECS || 0), 0);
                    return <span
                        className="cursor-pointer ">{noRoomECS}</span>;
                }} />}
                {visibleColumns.includes("ReqHoldByPV") && <Column field="reqHoldPV" header="ReqHoldByPV" body={reqHoldByPV} sortable footer={() => {
                    const reqHoldPV = tableData.tableData.reduce((sum, item) => sum + (item.reqHoldPV || 0), 0);
                    return <span
                        className="cursor-pointer ">{reqHoldPV}</span>;
                }} />}
            </DataTable>
            <SubTable open={subTable.open} salesID={subTable.salesID} resType={subTable.resType} hideDialog={hideSubTableDialog} toast={toast} salEffFilter={salEffFilter} />
            <Toast ref={toast} />
        </>
    );
}
